import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { CompareSeries, SymbolOut } from "../api/types";

const RECENT_KEY = "stux:compare:recent";
const RECENT_MAX = 3;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string").slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(symbol: string): string[] {
  const cur = loadRecent().filter((s) => s !== symbol);
  const next = [symbol, ...cur].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
  return next;
}

interface Props {
  open: boolean;
  yahooA: string;
  valueB: string | null;
  onPick: (symbol: string) => void;
  onClose: () => void;
  compare?: CompareSeries[];
}

export default function CompareDrawer({ open, yahooA, valueB, onPick, onClose, compare }: Props) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [recent, setRecent] = useState<string[]>(() => loadRecent());
  const inputRef = useRef<HTMLInputElement | null>(null);

  const sym = useQuery<SymbolOut[]>({
    queryKey: ["symsearch", query],
    queryFn: async () =>
      (await api.get<SymbolOut[]>("/symbols/search", { params: { q: query } })).data,
    enabled: open && query.length >= 1,
    staleTime: 60_000,
  });

  // Filter A out of suggestions — comparing AAPL vs AAPL is silly.
  const matches = useMemo(
    () => (sym.data ?? []).filter((s) => s.yahoo_symbol !== yahooA).slice(0, 8),
    [sym.data, yahooA]
  );

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Esc closes drawer; only when drawer is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  function commit(symbol: string) {
    setRecent(pushRecent(symbol));
    setQuery("");
    onPick(symbol);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % matches.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commit(matches[highlight].yahoo_symbol);
    }
  }

  const b = compare?.find((s) => s.yahoo_symbol === valueB);

  return (
    <div
      className={`grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out ${
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      }`}
      aria-hidden={!open}
    >
      <div className="min-h-0">
        <section className="mb-4 rounded-2xl border border-slate-800 bg-slate-900 p-3 sm:p-4">
          <header className="mb-2 flex items-center justify-between gap-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Compare <span className="font-mono text-slate-300">{yahooA}</span> vs.
            </div>
            <button
              onClick={onClose}
              aria-label="Close compare"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-800 text-slate-500 hover:border-slate-700 hover:text-slate-300"
            >
              <svg viewBox="0 0 14 14" className="h-3 w-3" aria-hidden="true">
                <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </header>

          <div className="relative">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search ticker or name…"
              inputMode="search"
              autoComplete="off"
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm placeholder:text-slate-500 focus:border-sky-500 focus:outline-none"
            />
            {query && matches.length > 0 && (
              <ul className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border border-slate-800 bg-slate-950 text-sm shadow-xl">
                {matches.map((s, i) => (
                  <li
                    key={s.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commit(s.yahoo_symbol);
                    }}
                    onMouseEnter={() => setHighlight(i)}
                    className={`flex cursor-pointer items-center justify-between gap-2 border-b border-slate-800 px-3 py-2 last:border-b-0 ${
                      i === highlight ? "bg-slate-800/70" : "hover:bg-slate-800/40"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-slate-200">{s.ticker}</span>
                      <span className="ml-2 text-slate-400">{s.name}</span>
                    </div>
                    <span className="text-[11px] text-slate-500">
                      {s.region} · {s.exchange}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {recent.length > 0 && !query && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-slate-600">Recent</span>
              {recent.map((r) => (
                <button
                  key={r}
                  onClick={() => commit(r)}
                  className={`rounded-full border px-2 py-0.5 font-mono text-[11px] transition ${
                    valueB === r
                      ? "border-sky-500/50 bg-sky-500/10 text-sky-300"
                      : "border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-700 hover:text-slate-200"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          )}

          {valueB && (
            <div className="mt-3 border-t border-slate-800 pt-3">
              {b ? (
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                  <div className="font-mono text-sm text-amber-300">{b.yahoo_symbol}</div>
                  <div className="min-w-0 flex-1 truncate text-slate-400">{b.name}</div>
                  <div>
                    <span className="text-slate-500">Last </span>
                    <span className="font-mono tabular-nums text-slate-200">
                      {b.last_close?.toFixed(2) ?? "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Score </span>
                    <span className="font-mono tabular-nums text-slate-200">
                      {b.score_medium?.toFixed(0) ?? "—"}
                    </span>
                    {b.stance_medium && (
                      <span className="ml-1 text-slate-500">· {b.stance_medium}</span>
                    )}
                  </div>
                  <div className="text-slate-600">{b.region}</div>
                </div>
              ) : (
                <div className="text-[11px] text-slate-500">Loading {valueB}…</div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
